import { createSwiper } from '@nulo/swiper';

/**
 * @param {string} template
 * @param {Record<string, string | number>} vars
 */
function interpolate(template, vars) {
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => String(vars[key] ?? ''));
}

/**
 * @extends {HTMLElement}
 */
class BlogFeaturedHero extends HTMLElement {
  /** @type {import('swiper').Swiper | null} */
  #swiper = null;

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    if (this.#swiper) return;

    const container = this.querySelector('[data-swiper]');
    if (!(container instanceof HTMLElement)) return;

    const slides = container.querySelectorAll('.swiper-slide');
    if (slides.length <= 1) return;

    this.#abortController = new AbortController();
    const paginationEl = container.querySelector('[data-pagination]');
    const slideNamedTemplate = this.dataset.i18nSlideNamed || 'Slide {{ index }}: {{ title }}';
    const slideNumberTemplate = this.dataset.i18nSlideNumber || 'Slide {{ index }}';

    /** @type {import('swiper').PaginationOptions | false} */
    const paginationConfig = paginationEl
      ? {
          el: paginationEl,
          clickable: true,
          renderBullet: (index, className) => {
            const slide = slides[index];
            const title = slide?.querySelector('[data-slide-title]')?.textContent?.trim() || '';
            const slideIndex = index + 1;
            const ariaLabel = title
              ? interpolate(slideNamedTemplate, { index: slideIndex, title })
              : interpolate(slideNumberTemplate, { index: slideIndex });

            return `<button type="button" class="${className}" aria-label="${ariaLabel}" aria-current="false" role="listitem"></button>`;
          },
          bulletClass: 'blog-featured-hero__bullet',
          bulletActiveClass: 'blog-featured-hero__bullet--active',
        }
      : false;

    this.#swiper = createSwiper({
      region: this,
      el: container,
      activeSlideClass: 'swiper-slide-active',
      config: {
        slidesPerView: 1,
        slidesPerGroup: 1,
        speed: 500,
        loop: true,
        keyboard: {
          enabled: true,
          onlyInViewport: true,
        },
        a11y: {
          enabled: true,
        },
        pagination: paginationConfig,
        navigation: false,
        autoplay: false,
      },
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;

    this.#swiper?.destroy?.(true, true);
    this.#swiper = null;
  }
}

if (!customElements.get('blog-featured-hero')) {
  customElements.define('blog-featured-hero', BlogFeaturedHero);
}
